import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  getJoinHint,
  getMarkets,
  getStatus,
  shopifyPlanChargeUrl,
  shopifyPricingPlansUrl,
  syncBilling,
  type SafegeoStatus,
} from "../lib/safegeo-api";
import { formatNumber, isFreePlan, planLabel } from "../lib/format";
import {
  recommendPlan,
  storesLabelToAnswer,
  type RecommendKey,
} from "../lib/recommend-plan";
import { queryKeys } from "../lib/query-keys";
import { SafePage } from "../components/SafePage";
import { PlanAccessoryBadge } from "../components/PhaseBadge";
import { PageLoading } from "../components/PageLoading";
import { AppButton } from "../components/AppButton";
import { ErrorBanner, InfoBanner } from "../components/PageBanner";

const APP_HANDLE = import.meta.env.VITE_SHOPIFY_APP_HANDLE || "safegeo";
const SUPPORT_EMAIL = "support@getpickads.com";
const CONFIRM_MODAL_ID = "sg-plan-confirm";

const PAID = [
  {
    key: "safegeo-starter",
    recKey: "starter" as RecommendKey,
    name: "Starter",
    monthly: 9.99,
    yearlyMonth: 7.99,
    yearly: 95.88,
    blurb:
      "For stores sending visitors elsewhere or blocking certain countries.",
    points: [
      "Unlimited automatic redirects",
      "Redirect to other websites",
      "Block country with reference code",
      "Custom CSS",
      "Configuration history",
    ],
  },
  {
    key: "safegeo-multi-store",
    recKey: "multi" as RecommendKey,
    name: "Multi-store",
    monthly: 29,
    yearlyMonth: 23.2,
    yearly: 278.4,
    blurb: "For regional brands with up to 4 Shopify stores.",
    points: [
      "Everything in Starter",
      "Link up to 4 stores",
      "Conflict check: no redirect loops",
      "Publish network",
    ],
  },
  {
    key: "safegeo-scale",
    recKey: "scale" as RecommendKey,
    name: "Scale",
    monthly: 59,
    yearlyMonth: 47.2,
    yearly: 566.4,
    blurb: "For larger networks managing up to 10 Shopify stores.",
    points: [
      "Everything in Multi-store",
      "Link up to 10 stores",
    ],
  },
] as const;

const FREE_POINTS = [
  "300 automatic redirects / month",
  "Ask first, always unlimited",
  "Market redirect and popup selector",
  "Test mode, bots and ads handled",
] as const;

type StoresLabel = "1" | "2-4" | "5+";
type Interval = "month" | "year";
type PendingChoice =
  | { kind: "paid"; planKey: string; name: string }
  | { kind: "free" };

function money(n: number) {
  const s = (Math.round(n * 100) / 100).toFixed(2).replace(/\.00$/, "");
  return `$${s}`;
}

function planMatchesKey(current: string, recKey: RecommendKey) {
  const p = current.toLowerCase();
  if (recKey === "multi") return p.includes("multi");
  return p.includes(recKey);
}

function monthLabel(d = new Date()) {
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

function nextResetLabel(d = new Date()) {
  const next = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1));
  return next.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function openModal(id: string) {
  const el = document.getElementById(id) as
    | (HTMLElement & { showOverlay?: () => void })
    | null;
  el?.showOverlay?.();
}

export function PlansPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  /** `/app/billing` keeps app nav (v4); `/app/plans` is first-pick / unlock. */
  const isBillingRoute = location.pathname.includes("/billing");
  const [status, setStatus] = useState<SafegeoStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [marketsCount, setMarketsCount] = useState(1);
  const [shopifyPlus, setShopifyPlus] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [doneText, setDoneText] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingChoice | null>(null);
  const [interval, setInterval] = useState<Interval>("month");
  const [stores, setStores] = useState<StoresLabel>(() => {
    const hint = (params.get("plan") || "").toLowerCase();
    if (hint.includes("multi")) return "2-4";
    if (hint.includes("scale")) return "5+";
    return "1";
  });
  const [joinHint, setJoinHint] = useState<{
    primary_name?: string;
    primary_domain?: string;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, markets, hint] = await Promise.all([
          getStatus(),
          getMarkets().catch(() => null),
          getJoinHint().catch(() => null),
        ]);
        if (cancelled) return;
        setStatus(s);
        setMarketsCount(
          Math.max(1, markets?.count || markets?.countries?.length || 1),
        );
        setShopifyPlus(!!markets?.shopify_plus);
        if (
          !isFreePlan(s.plan, s.plan_key) &&
          s.billing_interval === "year"
        ) {
          setInterval("year");
        } else if (
          !isFreePlan(s.plan, s.plan_key) &&
          s.billing_interval === "month"
        ) {
          setInterval("month");
        }
        if (hint?.suggest) {
          setJoinHint({
            primary_name: hint.primary_name,
            primary_domain: hint.primary_domain,
          });
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const currentLabel = planLabel(status?.plan || status?.plan_key);
  const onPaid = !isFreePlan(status?.plan, status?.plan_key);
  const firstPick =
    !!status?.needs_plan_pick ||
    (!onPaid && status?.plan_selected !== true);
  const yearly = interval === "year";

  const rec = useMemo(
    () =>
      recommendPlan({
        storesAnswer: storesLabelToAnswer(stores),
        marketsCount,
        shopifyPlus,
      }),
    [stores, marketsCount, shopifyPlus],
  );

  const redirectsUsed = status?.redirects_month ?? 0;
  const redirectCap = status?.redirects_cap;
  const trialEndsAt = status?.trial_ends_at
    ? new Date(status.trial_ends_at)
    : null;
  const trialActive =
    onPaid && !!trialEndsAt && trialEndsAt.getTime() > Date.now();
  /** One-shot trial: available only when Free and shop never started a trial. */
  const trialAvailable = !onPaid && !status?.trial_used_at;
  const trialDaysLeft = trialActive
    ? Math.max(
        0,
        Math.ceil(
          (trialEndsAt!.getTime() - Date.now()) / (24 * 3600 * 1000),
        ),
      )
    : 0;
  const trialEndLabel = trialEndsAt
    ? trialEndsAt.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })
    : "";
  const trialPrice = (() => {
    const p = (status?.plan || status?.plan_key || "").toLowerCase();
    if (p.includes("scale")) return money(59);
    if (p.includes("multi")) return money(29);
    if (p.includes("starter")) return money(9.99);
    return money(9.99);
  })();

  const usageLine =
    redirectCap != null
      ? `${monthLabel()} · ${formatNumber(redirectsUsed)} / ${formatNumber(redirectCap)} automatic redirects`
      : `${monthLabel()} · ${formatNumber(redirectsUsed)} automatic redirects, unlimited`;

  const progressValue =
    redirectCap != null && redirectCap > 0
      ? Math.min(redirectCap, redirectsUsed)
      : null;

  const curPlanHeading = trialActive
    ? `${currentLabel} (free trial)`
    : currentLabel || "Free";

  const pageHeading = "Plan";
  const planAccessory = (
    <PlanAccessoryBadge
      label={
        trialActive
          ? `${currentLabel} · Free trial`
          : `${currentLabel || "Free"} plan`
      }
    />
  );

  async function chooseFree() {
    setBusy(true);
    setError(null);
    try {
      const result = await syncBilling({
        plan_key: "safegeo-free",
        billing_interval: "month",
      });
      if (result.pricing_hint) {
        setError(result.pricing_hint);
        return;
      }
      await qc.invalidateQueries({ queryKey: queryKeys.status });
      await qc.refetchQueries({ queryKey: queryKeys.status });
      setDoneText("You’re now on Free.");
      navigate("/app", { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function openPlanCharge(planKey: string) {
    const shop = status?.shop_domain;
    if (!shop) {
      setError("Couldn’t find your shop. Reopen the app from Shopify Admin.");
      return;
    }
    window.open(
      shopifyPlanChargeUrl(shop, APP_HANDLE, planKey, interval),
      "_top",
    );
  }

  function openHostedPricing() {
    const shop = status?.shop_domain;
    if (!shop) {
      setError("Couldn’t find your shop. Reopen the app from Shopify Admin.");
      return;
    }
    window.open(shopifyPricingPlansUrl(shop, APP_HANDLE), "_top");
  }

  function askConfirm(choice: PendingChoice) {
    setPending(choice);
    setDoneText(null);
    requestAnimationFrame(() => openModal(CONFIRM_MODAL_ID));
  }

  function confirmChange() {
    if (!pending) return;
    const choice = pending;
    setPending(null);
    if (choice.kind === "free") {
      if (onPaid) {
        openHostedPricing();
        return;
      }
      void chooseFree();
      return;
    }
    openPlanCharge(choice.planKey);
  }

  const pendTitle = !pending
    ? ""
    : pending.kind === "free"
      ? "Change to Free?"
      : trialAvailable
        ? `Start your 14-day free trial of ${pending.name}?`
        : `Change to ${pending.name}?`;

  const pendNote = (() => {
    if (!pending) return "";
    if (pending.kind === "paid" && trialAvailable) {
      const price =
        PAID.find((p) => p.key === pending.planKey)?.monthly ?? 9.99;
      return `You won’t be charged during the trial, then ${money(price)} per month. Switch to Free before then and you pay nothing.`;
    }
    if (pending.kind === "free" && onPaid) {
      return "Block and website rules are paused, not deleted. Linked stores are disconnected; their rules are paused, not deleted.";
    }
    return "";
  })();

  if (loading && !status) {
    return (
      <SafePage screen="plans" heading={pageHeading}>
        <PageLoading label="Loading plans…" />
      </SafePage>
    );
  }

  return (
    <SafePage
      screen="plans"
      heading={pageHeading}
      accessory={planAccessory}
    >
      {joinHint ? (
        <InfoBanner
          heading={`Is this store part of ${joinHint.primary_name || "your primary store"}?`}
        >
          Join it as a secondary store. No extra plan needed.{" "}
          <AppButton variant="primary" to="/app/stores">
            Join as a secondary store
          </AppButton>
        </InfoBanner>
      ) : null}

      {error ? (
        <ErrorBanner heading="Couldn’t update plan">{error}</ErrorBanner>
      ) : null}

      {doneText ? (
        <s-banner tone="success" heading="Plan changed.">
          {doneText}
        </s-banner>
      ) : null}

      {trialActive ? (
        <InfoBanner
          heading={`Your free trial ends ${trialEndLabel} (${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"} left).`}
        >
          After that, {trialPrice} per month on your Shopify invoice. Switch to
          Free before then and you won’t be charged.{" "}
          <AppButton
            variant="secondary"
            onClick={() => askConfirm({ kind: "free" })}
          >
            Switch to Free
          </AppButton>
        </InfoBanner>
      ) : null}

      {!firstPick ? (
        <s-section>
          <s-stack gap="base">
            <s-stack
              direction="inline"
              gap="base"
              alignItems="baseline"
              justifyContent="space-between"
            >
              <s-heading>Current plan: {curPlanHeading}</s-heading>
              <s-text color="subdued">{usageLine}</s-text>
            </s-stack>
            {progressValue != null && redirectCap != null ? (
              <s-progress
                value={progressValue}
                max={redirectCap}
                accessibilityLabel={usageLine}
              />
            ) : null}
            <s-paragraph color="subdued">
              Only automatic redirects are metered. A visitor who picks a
              country in the popup never counts. Resets {nextResetLabel()}.
            </s-paragraph>
            {!isBillingRoute ? (
              <AppButton variant="tertiary" to="/app">
                Back to Home
              </AppButton>
            ) : null}
          </s-stack>
        </s-section>
      ) : null}

      <s-section>
        <s-stack gap="base">
          <s-stack direction="inline" gap="small" alignItems="center">
            <s-text color="subdued">Your stores</s-text>
            {(
              [
                { id: "1" as const, label: "1 store" },
                { id: "2-4" as const, label: "2 to 4" },
                { id: "5+" as const, label: "5 to 10" },
              ] as const
            ).map((opt) => (
              <s-button
                key={opt.id}
                type="button"
                variant={stores === opt.id ? "primary" : "secondary"}
                disabled={busy}
                onClick={() => setStores(opt.id)}
              >
                {opt.label}
              </s-button>
            ))}
          </s-stack>
          <s-stack direction="inline" gap="small" alignItems="center">
            <s-text color="subdued">Billing</s-text>
            <s-button
              type="button"
              variant={!yearly ? "primary" : "secondary"}
              disabled={busy}
              onClick={() => setInterval("month")}
            >
              Monthly
            </s-button>
            <s-button
              type="button"
              variant={yearly ? "primary" : "secondary"}
              disabled={busy}
              onClick={() => setInterval("year")}
            >
              Yearly (−20%)
            </s-button>
          </s-stack>
        </s-stack>
      </s-section>

      <s-section>
        <s-grid gridTemplateColumns="1fr 1fr 1fr" gap="base">
          {PAID.map((plan) => {
            const recommended = plan.recKey === rec.plan;
            const isCurrent = planMatchesKey(
              status?.plan || status?.plan_key || "",
              plan.recKey,
            );
            const monthEq = yearly
              ? money(plan.yearlyMonth)
              : money(plan.monthly);
            const billedNote = yearly
              ? `${money(plan.yearly)} billed yearly (saves 20%)`
              : "billed monthly";
            const cta = isCurrent
              ? "Current plan"
              : trialAvailable
                ? "Start 14-day free trial"
                : `Choose ${plan.name}`;
            return (
              <s-box
                key={plan.key}
                padding="base"
                borderWidth="base"
                borderRadius="base"
                borderColor={isCurrent || recommended ? "strong" : undefined}
              >
                <s-stack direction="block" gap="small">
                  <s-stack
                    direction="inline"
                    gap="small"
                    alignItems="center"
                  >
                    <s-heading>{plan.name}</s-heading>
                    {isCurrent ? (
                      <s-badge tone="info">Current</s-badge>
                    ) : recommended ? (
                      <s-badge>Recommended</s-badge>
                    ) : null}
                  </s-stack>
                  <s-paragraph color="subdued">{plan.blurb}</s-paragraph>
                  <s-stack
                    direction="inline"
                    gap="small"
                    alignItems="baseline"
                  >
                    <s-heading>{monthEq}</s-heading>
                    <s-text color="subdued">/ month</s-text>
                  </s-stack>
                  <s-paragraph color="subdued">{billedNote}</s-paragraph>
                  {plan.points.map((pt) => (
                    <s-paragraph key={pt}>✓ {pt}</s-paragraph>
                  ))}
                  {isCurrent ? (
                    <s-button variant="secondary" disabled>
                      Current plan
                    </s-button>
                  ) : (
                    <s-button
                      variant={recommended ? "primary" : "secondary"}
                      disabled={busy}
                      onClick={() =>
                        askConfirm({
                          kind: "paid",
                          planKey: plan.key,
                          name: plan.name,
                        })
                      }
                    >
                      {cta}
                    </s-button>
                  )}
                </s-stack>
              </s-box>
            );
          })}
        </s-grid>
      </s-section>

      <s-section>
        <s-box
          padding="base"
          borderWidth="base"
          borderRadius="base"
          borderColor={!onPaid ? "strong" : undefined}
        >
          <s-stack
            direction="inline"
            gap="base"
            alignItems="center"
            justifyContent="space-between"
          >
            <s-stack direction="block" gap="small">
              <s-stack direction="inline" gap="small" alignItems="baseline">
                <s-heading>Free</s-heading>
                <s-text color="subdued">$0 forever</s-text>
              </s-stack>
              <s-paragraph color="subdued">
                For one store that needs the right currency and language for
                each visitor.
              </s-paragraph>
              <s-grid gridTemplateColumns="1fr 1fr" gap="small">
                {FREE_POINTS.map((pt) => (
                  <s-paragraph key={pt}>✓ {pt}</s-paragraph>
                ))}
              </s-grid>
            </s-stack>
            {!onPaid && !firstPick ? (
              <s-button variant="secondary" disabled>
                Current plan
              </s-button>
            ) : (
              <s-button
                variant="secondary"
                disabled={busy}
                loading={busy && pending?.kind === "free"}
                onClick={() => askConfirm({ kind: "free" })}
              >
                {onPaid || trialActive ? "Switch to Free" : "Start with Free"}
              </s-button>
            )}
          </s-stack>
        </s-box>
      </s-section>

      <s-box paddingBlockStart="base">
        <s-paragraph color="subdued">
          Billed on your Shopify invoice. Changes apply right away and your
          rules are kept. Unlimited means no monthly redirect cap, with fair
          use for abuse. Development and partner stores are free. Questions
          about billing?{" "}
          <s-link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</s-link>
        </s-paragraph>
      </s-box>

      <s-modal id={CONFIRM_MODAL_ID} heading={pendTitle || "Change plan?"}>
        <s-paragraph>
          Billed on your Shopify invoice. The change applies right away and
          your rules are kept.
          {pendNote ? ` ${pendNote}` : ""}
        </s-paragraph>
        <AppButton
          slot="secondary-actions"
          commandFor={CONFIRM_MODAL_ID}
          command="--hide"
          onClick={() => setPending(null)}
        >
          Cancel
        </AppButton>
        <AppButton
          slot="primary-action"
          commandFor={CONFIRM_MODAL_ID}
          command="--hide"
          loading={busy}
          onClick={confirmChange}
        >
          Confirm change
        </AppButton>
      </s-modal>
    </SafePage>
  );
}
